import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/session';
import { ToastProvider, Skeleton, Card } from './ui/components';
import { AdminShell, MemberShell } from './ui/Shell';
import Login, { ProfileForm } from './features/Login';
import type { Permission } from './domain/index.js';
import { t } from './i18n';
import { PAYMENTS_ENABLED } from './lib/features';

const Today = lazy(() => import('./features/admin/Today'));
const CoachOS = lazy(() => import('./features/admin/CoachOS'));
const Members = lazy(() => import('./features/admin/Members'));
const MemberProfile = lazy(() => import('./features/admin/MemberProfile'));
const Roster = lazy(() => import('./features/admin/Roster'));
const Schedule = lazy(() => import('./features/admin/Schedule'));
const Progress = lazy(() => import('./features/admin/Progress'));
const Leads = lazy(() => import('./features/admin/Ops').then(m => ({ default: m.Leads })));
const Tasks = lazy(() => import('./features/admin/Ops').then(m => ({ default: m.Tasks })));
const Billing = lazy(() => import('./features/admin/Ops').then(m => ({ default: m.FailedPayments })));
const Settings = lazy(() => import('./features/settings/Settings'));
const Kiosk = lazy(() => import('./features/kiosk/Kiosk'));
const MHome = lazy(() => import('./features/member/Home'));
const MSchedule = lazy(() => import('./features/member/Schedule'));
const MProgress = lazy(() => import('./features/member/Progress'));
const MMembership = lazy(() => import('./features/member/Membership'));
const MProfile = lazy(() => import('./features/member/Profile'));

const Loading = () => <div style={{ padding: 24 }}><Skeleton lines={4} /></div>;

function Guard({ perm, staff, member, children }: { perm?: Permission; staff?: boolean; member?: boolean; children: ReactNode }) {
  const s = useSession();
  if (s.loading) return <Loading />;
  if (!s.user) return <Login />;
  if (staff && !s.staff) return <Navigate to="/m" replace />;
  if (member && !s.memberId && !s.staff) return <div className="center"><Card className="stack"><h2>{t('auth.completeTitle')}</h2><ProfileForm onDone={() => location.reload()} /></Card></div>;
  if (member && !s.memberId && s.staff) return <Navigate to="/a" replace />;
  if (perm && !s.can(perm)) return <div className="empty">{t('errors.permission')}</div>;
  return <>{children}</>;
}
function PaymentsGuard({ children }: { children: ReactNode }) {
  if (!PAYMENTS_ENABLED) return <Navigate to="/a" replace />;
  return <>{children}</>;
}

function Home() { const s = useSession(); if (s.loading) return <Loading />; if (!s.user) return <Login />; return <Navigate to={s.staff ? '/a' : '/m'} replace />; }

export default function App() {
  return <SessionProvider><ToastProvider><BrowserRouter><Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/kiosk" element={<Guard perm="attendance.checkInAny"><Kiosk /></Guard>} />
      <Route path="/a" element={<Guard staff><AdminShell /></Guard>}>
        <Route index element={<Today />} />
        <Route path="coach" element={<CoachOS />} />
        <Route path="members" element={<Guard perm="members.read"><Members /></Guard>} />
        <Route path="members/:id" element={<Guard perm="members.read"><MemberProfile /></Guard>} />
        <Route path="roster/:sessionId" element={<Guard perm="attendance.checkInAny"><Roster /></Guard>} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="progress" element={<Guard perm="progress.assess"><Progress /></Guard>} />
        <Route path="leads" element={<Guard perm="leads.manage"><Leads /></Guard>} />
        <Route path="tasks" element={<Guard perm="tasks.manage"><Tasks /></Guard>} />
        <Route path="billing" element={<PaymentsGuard><Guard perm="billing.read"><Billing /></Guard></PaymentsGuard>} />
        <Route path="settings" element={<Guard perm="settings.manage"><Settings /></Guard>} />
        <Route path="*" element={<Navigate to="/a" replace />} />
      </Route>
      <Route path="/m" element={<Guard member><MemberShell /></Guard>}>
        <Route index element={<MHome />} /><Route path="schedule" element={<MSchedule />} /><Route path="progress" element={<MProgress />} />
        <Route path="membership" element={<MMembership />} /><Route path="profile" element={<MProfile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes></Suspense></BrowserRouter></ToastProvider></SessionProvider>;
}
